// pipe: shared ERP date controls (SSOT / DRY). ONE token-styled native date input and ONE from/to
// range, used by every ERP screen that takes a date — the Reports param form, the Customer-360
// upload dialog and the Operations position map. They previously each hand-rolled the markup over a
// near-identical private `dateInput` style (three copies, already drifted in padding); this is the
// single definition, following the shared-module pattern of erp-page-header / erp-table.
//
// It owns the three things a caller kept getting wrong:
//   1. the token styling (light/dark inherited — never a hardcoded colour),
//   2. the from/to accessible names (localized ONCE here, not per screen), and
//   3. the SIZING that makes a native `input[type=date]` shrink inside a grid track or flex row
//      instead of overflowing it and painting over its neighbour (see erp-date-field.css.ts).
//
// Import discipline (mirrors the other pure view seams): css module + @affine/i18n only — no
// runtime services/DI/workbench — so it renders cleanly under happy-dom.
import { useI18n } from '@affine/i18n';

import * as styles from './erp-date-field.css';

/** The one place the native control is spelled out; the exports below only choose its sizing. */
function DateControl({
  className,
  value,
  onChange,
  label,
}: {
  className: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <input
      type="date"
      className={className}
      aria-label={label}
      value={value}
      onChange={e => onChange(e.currentTarget.value)}
    />
  );
}

export interface ErpDateInputProps {
  /** ISO `YYYY-MM-DD`; the empty string is "unset". */
  value: string;
  /** Receives the raw input value — ISO `YYYY-MM-DD`, or '' when cleared. */
  onChange: (value: string) => void;
  /** Accessible name. The control carries no visible caption of its own. */
  label: string;
}

/** One date control, filling the field column it is placed in. */
export function ErpDateInput({ value, onChange, label }: ErpDateInputProps) {
  return (
    <DateControl
      className={styles.dateInput}
      value={value}
      onChange={onChange}
      label={label}
    />
  );
}

/** The from/to pair a date-range filter edits. Both bounds are optional (an open-ended range). */
export interface ErpDateRangeValue {
  from: string;
  to: string;
}

export interface ErpDateRangeProps {
  value: ErpDateRangeValue;
  /** Receives the WHOLE range — the untouched bound is carried through unchanged. */
  onChange: (next: ErpDateRangeValue) => void;
  /** Accessible-name stem; each bound is named `<label> — From` / `<label> — To`. */
  label: string;
  /** Render the localized From/To captions before each control. Use where the pair sits in a
   *  toolbar row with no field label above it (the position map); leave off inside a `formField`
   *  column, whose label already names the range. */
  inlineCaptions?: boolean;
}

/** Two date controls that shrink and wrap inside whatever cell they are given. */
export function ErpDateRange({
  value,
  onChange,
  label,
  inlineCaptions = false,
}: ErpDateRangeProps) {
  const t = useI18n();
  const fromWord = t['com.affine.pipe.date-range.from']();
  const toWord = t['com.affine.pipe.date-range.to']();

  return (
    <div className={styles.dateRangeRow}>
      {inlineCaptions && (
        <span className={styles.dateRangeCaption}>{fromWord}</span>
      )}
      <DateControl
        className={styles.dateRangeInput}
        value={value.from}
        onChange={from => onChange({ ...value, from })}
        label={`${label} — ${fromWord}`}
      />
      {inlineCaptions && (
        <span className={styles.dateRangeCaption}>{toWord}</span>
      )}
      <DateControl
        className={styles.dateRangeInput}
        value={value.to}
        onChange={to => onChange({ ...value, to })}
        label={`${label} — ${toWord}`}
      />
    </div>
  );
}
